(() => {
  'use strict';
  const KEY = 'BosscoElectricLanding';
  if (window[KEY]) { window[KEY].scan(); return; }
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  let paused = false, frame = 0, reveals, storyPreload, stories = [], curtains = [], parallax = [], active = false;
  let purchase, purchaseObserver, purchaseContainer, purchaseForm, syncing = false, cartErrorsBound = false;
  const watchedStageImages = new WeakSet(), decodingStageImages = new WeakSet(), decodedStageImages = new WeakSet();
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const html = document.documentElement;
  const previewView = new URL(location.href).searchParams.get('view');
  function decodeStageImage(image) {
    if (!image.complete || !image.naturalWidth || decodedStageImages.has(image) || decodingStageImages.has(image)) return;
    decodingStageImages.add(image);
    const decoded = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
    decoded.catch(() => {}).then(() => {
      decodingStageImages.delete(image);
      if (image.complete && image.naturalWidth) decodedStageImages.add(image);
      schedule();
    });
  }
  function prepareStoryImages(story) {
    story.querySelectorAll('[data-bc-story-image] img').forEach(image => {
      if (!watchedStageImages.has(image)) {
        watchedStageImages.add(image);
        image.addEventListener('load', () => {
          decodedStageImages.delete(image);
          decodeStageImage(image);
          schedule();
        });
      }
      decodeStageImage(image);
    });
  }
  function preloadStory(story) {
    if (story.dataset.bcEnhanced !== 'true') return;
    // Clipped scenes can stay outside the browser's lazy-load area until activated.
    story.querySelectorAll('[data-bc-story-image] img').forEach(image => { image.loading = 'eager'; });
    prepareStoryImages(story);
  }
  function scan() {
    const hero = document.querySelector('[data-bc-electric-landing]');
    const motion = !!hero && !reduce.matches && !paused && hero.dataset.bcMotion !== 'false';
    active = !!hero;
    html.classList.toggle('bc-landing-page', !!hero);
    html.classList.toggle('bc-electric-landing-page', !!hero);
    html.classList.toggle('bc-motion-on', active && motion);
    reveals?.disconnect();
    if (active && motion && 'IntersectionObserver' in window) {
      reveals = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('bc-in-view'); reveals.unobserve(entry.target); }
      }), { threshold: .08, rootMargin: '0px 0px -20px 0px' });
      document.querySelectorAll('[data-bc-reveal]').forEach(node => reveals.observe(node));
    } else document.querySelectorAll('[data-bc-reveal]').forEach(node => node.classList.add('bc-in-view'));
    storyPreload?.disconnect();
    storyPreload = null;
    if (motion && innerWidth >= 900 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        preloadStory(entry.target);
        observer.unobserve(entry.target);
      }), { rootMargin: '1000px 0px', threshold: 0 });
      storyPreload = observer;
    }
    stories = Array.from(document.querySelectorAll('[data-bc-story]'));
    stories.forEach(story => {
      const enhanced = motion && innerWidth >= 900 && story.dataset.bcMotion !== 'false';
      story.dataset.bcEnhanced = String(enhanced);
      if (enhanced) {
        prepareStoryImages(story);
        if (storyPreload) storyPreload.observe(story);
        else preloadStory(story);
      }
    });
    curtains = Array.from(document.querySelectorAll('[data-bc-curtain]'));
    curtains.forEach(curtain => {
      curtain.dataset.bcCurtainEnhanced = String(motion && curtain.closest('[data-bc-story]')?.dataset.bcMotion !== 'false');
    });
    parallax = Array.from(document.querySelectorAll('[data-bc-parallax]'));
    document.querySelectorAll('[data-bc-motion-toggle]').forEach(button => {
      button.hidden = reduce.matches || hero?.dataset.bcMotion === 'false';
      button.setAttribute('aria-pressed', String(paused));
      button.innerHTML = paused ? 'Play motion <span aria-hidden="true">▷</span>' : 'Pause motion <span aria-hidden="true">Ⅱ</span>';
    });
    connectPurchase();
    tick();
  }
  function tick() {
    frame = 0;
    const motion = html.classList.contains('bc-motion-on');
    const hero = document.querySelector('[data-bc-electric-landing]');
    if (hero) {
      const box = hero.getBoundingClientRect();
      hero.style.setProperty('--bc-hero-scale', motion ? (1.025 + clamp(-box.top / box.height, 0, 1) * .11).toFixed(3) : '1');
      const progress = document.querySelector('[data-bc-page-progress]');
      progress?.style.setProperty('--bc-page-progress', String(clamp(scrollY / Math.max(1, document.documentElement.scrollHeight - innerHeight), 0, 1)));
    }
    document.querySelectorAll('.bc-landing-rail a').forEach(link => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      const box = target.getBoundingClientRect();
      link.style.setProperty('--bc-chapter-progress', String(clamp((innerHeight - box.top) / (innerHeight + box.height), 0, 1)));
    });
    for (const el of parallax) {
      if (!motion || innerWidth < 900) { el.style.removeProperty('--bc-parallax-y'); continue; }
      const box = el.parentElement.getBoundingClientRect();
      if (box.top > innerHeight || box.bottom < 0) continue;
      const amount = Math.max(-65, Math.min(65, (innerHeight / 2 - box.top - box.height / 2) * (Number(el.dataset.bcParallax) || .08)));
      el.style.setProperty('--bc-parallax-y', `${amount.toFixed(2)}px`);
    }
    for (const curtain of curtains) {
      if (curtain.dataset.bcCurtainEnhanced !== 'true') {
        curtain.style.removeProperty('--bc-curtain-y');
        curtain.dataset.bcCurtainProgress = '1';
        continue;
      }
      const pin = curtain.querySelector('.bc-electric-story__reveal-pin');
      const box = curtain.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(pin).top) || 0;
      const travel = Math.max(1, curtain.offsetHeight - pin.offsetHeight);
      const progress = clamp(((top - box.top) / travel - .07) / .8, 0, 1);
      curtain.style.setProperty('--bc-curtain-y', `${(-102 * progress).toFixed(3)}%`);
      curtain.dataset.bcCurtainProgress = progress.toFixed(3);
    }
    for (const story of stories) {
      const heading = story.querySelector('.bc-electric-story__title');
      if (heading) heading.style.setProperty('--bc-heading-drift', motion && innerWidth >= 900 && !heading.closest('[data-bc-curtain]') ? `${clamp((innerHeight / 2 - heading.getBoundingClientRect().top) * .06, -28, 28).toFixed(1)}px` : '0px');
      if (story.dataset.bcEnhanced !== 'true') continue;
      const steps = Array.from(story.querySelectorAll('[data-bc-story-step]'));
      let closest = 0, distance = Infinity, containing = -1;
      steps.forEach((step, index) => {
        const box = step.getBoundingClientRect();
        if (box.top <= innerHeight / 2 && box.bottom >= innerHeight / 2) containing = index;
        const d = Math.abs(box.top + box.height / 2 - innerHeight / 2);
        if (d < distance) { distance = d; closest = index; }
      });
      if (containing >= 0) closest = containing;
      const scenes = Array.from(story.querySelectorAll('[data-bc-story-image]'));
      const nextImage = scenes[closest]?.querySelector('img');
      // Keep the last visible photograph until its replacement is ready to paint.
      if (!nextImage || (nextImage.complete && nextImage.naturalWidth && decodedStageImages.has(nextImage))) {
        scenes.forEach((scene,index) => scene.dataset.active = String(index === closest));
      }
      steps.forEach((step,index) => step.dataset.active = String(index === closest));
    }
  }
  function revealPurchase() {
    const target = document.querySelector('#bc-electric-buy') || purchaseContainer;
    target?.scrollIntoView({behavior:html.classList.contains('bc-motion-on')?'smooth':'auto',block:'start'});
  }
  function syncPurchase() {
    if (!purchase?.isConnected || !purchaseForm?.isConnected || syncing) return;
    const id = purchaseForm.querySelector('[name="id"]')?.value;
    const variant = purchase.variants.find(item => String(item.id) === String(id));
    const nativeButton = purchaseForm.querySelector('button[type="submit"]');
    const price = purchase.querySelector('[data-bc-price]');
    const compare = purchase.querySelector('[data-bc-compare]');
    const select = purchase.querySelector('[data-bc-variant]');
    const button = purchase.querySelector('[data-bc-add]');
    const status = purchase.querySelector('[data-bc-purchase-status]');
    if (variant) {
      price.innerHTML = variant.price;
      compare.innerHTML = variant.compare || '';
      compare.hidden = !variant.compare;
      if (select) select.value = String(variant.id);
    }
    const busy = nativeButton?.classList.contains('btn--in-progress');
    button.disabled = !variant?.available || !nativeButton || nativeButton.disabled || busy;
    button.setAttribute('aria-busy',String(!!busy));
    const text = busy ? 'Adding…' : !variant ? 'Choose your options' : !variant.available ? 'Sold out' : nativeButton?.textContent.trim() || 'Add to cart';
    purchase.querySelector('[data-bc-add-label]').textContent = text;
    if (!purchase.dataset.bcError) status.textContent = !variant ? 'Choose a configuration below.' : !variant.available ? 'Currently sold out. Check availability below.' : variant.backorder ? 'Available on backorder. Check delivery details below.' : 'Available to order';
  }
  function connectPurchase() {
    const panel = document.querySelector('[data-bc-electric-landing] [data-bc-purchase]');
    const container = Array.from(document.querySelectorAll('[data-section-type="main-product"] .product-form')).find(node => node.dataset.productId === panel?.dataset.productId);
    const form = container?.querySelector('form.product-purchase-form');
    if (!panel || !container || !form) { purchaseObserver?.disconnect(); purchase = purchaseContainer = purchaseForm = null; return; }
    if (panel === purchase && container === purchaseContainer && form === purchaseForm) { syncPurchase(); return; }
    purchaseObserver?.disconnect();
    purchase = panel; purchaseContainer = container; purchaseForm = form;
    try { panel.variants = JSON.parse(panel.querySelector('[data-bc-variants]').textContent); } catch { return; }
    const ready = () => {
      if (!container.classList.contains('theme-init')) return;
      bindCartErrors();
      panel.querySelectorAll('[data-bc-purchase-controls]').forEach(node => node.hidden = false);
      panel.querySelector('[data-bc-purchase-fallback]').hidden = true;
      syncPurchase();
    };
    purchaseObserver = new MutationObserver(ready);
    purchaseObserver.observe(container,{subtree:true,childList:true,attributes:true,attributeFilter:['disabled','class','value']});
    ready();
  }
  function bindCartErrors() {
    if (cartErrorsBound || !window.jQuery) return;
    cartErrorsBound = true;
    window.jQuery(document).on('ajaxError.bcRoller', (_event, xhr, settings) => {
      if (!purchase?.isConnected || !/\/cart\/add/.test(settings.url || '')) return;
      let message = 'Unable to add this item. Check your selection and try again.';
      try { const response = JSON.parse(xhr.responseText); if (typeof response.description === 'string') message = response.description; } catch { /* Keep the useful fallback. */ }
      purchase.dataset.bcError = 'true';
      const errorStatus = purchase.querySelector('[data-bc-purchase-status]');
      errorStatus.textContent = message;
      errorStatus.hidden = false;
      syncPurchase();
    });
  }
  document.addEventListener('change', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('[data-bc-variant]') && purchase?.contains(event.target)) {
      const variant = purchase?.variants.find(item => String(item.id) === event.target.value);
      if (!variant || !purchaseForm) return;
      delete purchase.dataset.bcError;
      const currentView = new URL(location.href).searchParams.get('view');
      syncing = true;
      // Use Mode's own selector API so visible options, app listeners and variant state agree.
      purchaseContainer.querySelectorAll('.option-selector').forEach((selector,index) => {
        const value = variant.options[index];
        const custom = selector.querySelector('.cc-select');
        if (custom) custom.dispatchEvent(new CustomEvent('selectOption',{detail:{value}}));
        else {
          const radio = Array.from(selector.querySelectorAll('input.js-option')).find(input => input.value === value);
          if (radio && !radio.checked) radio.click();
        }
      });
      purchaseContainer.querySelectorAll('[name="id"]').forEach(input => {
        input.value = String(variant.id);
        input.dispatchEvent(new Event('change',{bubbles:true}));
      });
      // Mode updates the variant URL; keep this alternate-template preview selected.
      if (currentView) { const url = new URL(location.href); url.searchParams.set('view',currentView); history.replaceState(history.state,'',url); }
      syncing = false;
      syncPurchase();
    } else if (purchaseContainer?.contains(event.target)) {
      delete purchase.dataset.bcError;
      queueMicrotask(() => {
        if (previewView) { const url = new URL(location.href); url.searchParams.set('view',previewView); history.replaceState(history.state,'',url); }
        syncPurchase();
      });
    }
  });
  function schedule() { if (active && !frame) frame = requestAnimationFrame(tick); }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const add = event.target.closest('[data-bc-add]');
    if (add && purchase?.contains(add)) {
      const nativeButton = purchaseForm?.querySelector('button[type="submit"]');
      if (!nativeButton || nativeButton.disabled) { syncPurchase(); return; }
      if (!purchaseForm.checkValidity()) { revealPurchase(); purchaseForm.reportValidity(); return; }
      delete purchase.dataset.bcError;
      nativeButton.click();
      syncPurchase();
      return;
    }
    const toggle = event.target.closest('[data-bc-motion-toggle]');
    if (toggle?.closest('[data-bc-electric-landing]')) { paused = !paused; scan(); return; }
    const link = event.target.closest('.bc-landing a[href^="#bc-electric-"],.bc-landing-rail a[href^="#bc-electric-"],.bc-electric-story a[href^="#bc-electric-"]');
    if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: html.classList.contains('bc-motion-on') ? 'smooth' : 'auto', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex','-1');
    target.focus({ preventScroll: true });
  });
  addEventListener('scroll', schedule, { passive:true });
  addEventListener('resize', scan, { passive:true });
  reduce.addEventListener('change', scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', () => setTimeout(scan,0));
  document.addEventListener('shopify:section:reorder', scan);
  document.addEventListener('bc:recommendations:loaded',scan);
  document.addEventListener('shopify:block:select', event => {
    const step = event.target.closest('[data-bc-story-step]');
    if (step) { step.scrollIntoView({block:'center',behavior:'auto'}); schedule(); }
  });
  window[KEY] = {scan};
  addEventListener('load',connectPurchase,{once:true});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',scan,{once:true}); else scan();
})();
